function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState, useEffect, useRef } from 'react';
import { Info as InfoIcon } from '@mui/icons-material';
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormHelperText from "@mui/material/FormHelperText";
import { styled } from '@mui/system';
import { Tooltip, tooltipClasses } from '@mui/material';
import PropTypes from 'prop-types';
import * as dates from '@simplybenefits/sb-dates';
import Modal from './Modal.js';
import i18n from '../../localization/i18n.js';
import tokens from '../../localization/tokens.js';
import Text from '../Text.js';
import TextInput from '../../components/TextFields/TextInput.js';
import DateInput from '../../components/TextFields/DateInput.js';
import colors from '../../theme/colors.js';
import Files from '../../containers/Files/index.js';
import { validateInput } from '../../services/index.js';
const CustomWidthTooltip = styled(({
  className,
  ...props
}) => /*#__PURE__*/React.createElement(Tooltip, _extends({}, props, {
  classes: {
    popper: className
  }
})))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 860,
    fontSize: 14
  }
});
const initialErrorState = {
  nameError: "",
  signatureError: "",
  reasonForChangeError: "",
  effectiveDateError: "",
  acceptedTermsError: false
};
const initialState = {
  changeRequestUUID: null,
  changeRequestID: null,
  name: "",
  signature: "",
  reasonForChange: "",
  effectiveDate: "",
  acceptedTerms: false
};

/**
 * 
 * @param {*} param0 
 * @returns 
 */
const ChangeRequestModal = ({
  open,
  onClose,
  createChangeRequest,
  deleteChangeRequest,
  submitChangeRequest,
  changeRequestData,
  loader,
  type,
  id,
  dispatch,
  listFiles,
  createFolder,
  uploadFile,
  downloadFile,
  removeFile
}) => {
  const [changeRequest, setChangeRequest] = useState(initialState);
  const [errorState, setErrorState] = useState(initialErrorState);
  const acceptedTermsErrorRef = useRef(null);
  useEffect(() => {
    /**
     * Asynchronously initiates a change request.
     * 
     * This function is triggered when `changeRequestData` changes. It calls the `createChangeRequest` function,
     * which is expected to return an object containing `changeRequestUUID` and `changeRequestID`. If these values are valid,
     * it updates the `changeRequest` state with these IDs. In case of an error, it logs the error message to the console.
     */
    const initiateChangeRequest = async () => {
      try {
        const {
          changeRequestUUID,
          changeRequestID
        } = await createChangeRequest(changeRequestData);
        if (changeRequestID && changeRequestUUID) {
          setChangeRequest(prevState => ({
            ...prevState,
            changeRequestUUID,
            changeRequestID
          }));
        }
      } catch (error) {
        console.error("Error initiating change request:", error);
      }
    };
    changeRequestData && initiateChangeRequest();
  }, [changeRequestData]);
  const {
    nameError,
    signatureError,
    reasonForChangeError,
    effectiveDateError,
    acceptedTermsError
  } = errorState;
  const {
    changeRequestUUID,
    changeRequestID,
    name,
    signature,
    reasonForChange,
    effectiveDate,
    acceptedTerms
  } = changeRequest;

  /**
   * 
   * @param {*} field 
   * @param {*} value 
   */
  const handleInputChange = (field, value) => {
    setChangeRequest(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  /**
   * 
   * @param {*} field 
   * @param {*} value 
   */
  const onBlur = (field, value) => {
    let error = "";
    switch (field) {
      case "name":
      case "reasonForChange":
      case "signature":
        error = validateInput("checkEmpty", value);
        if (field === "signature" && changeRequest.name !== value) {
          error = i18n.t(tokens.labels.signage.signageNameError);
        }
        break;
      case "effectiveDate":
        error = validateInput("effectivedate", value, i18n.locale);
        break;
      default:
        break;
    }
    setErrorState(prevState => ({
      ...prevState,
      [`${field}Error`]: error
    }));
  };

  /**
   * 
   */
  const resetForm = () => {
    setErrorState(initialErrorState);
    setChangeRequest(initialState);
  };

  /**
   * Validates fields and sets error states
   * @returns {boolean} True if there are errors, otherwise false
   */
  const validateFields = async () => {
    const errors = {}; // Object to store validation errors

    errors.nameError = await validateInput("checkEmpty", name);
    errors.signatureError = await validateInput("checkEmpty", signature);
    if (signature !== name) {
      errors.signatureError = i18n.t(tokens.labels.signage.signageNameError);
    }
    errors.reasonForChangeError = await validateInput("checkEmpty", reasonForChange);
    errors.effectiveDateError = await validateInput("effectivedate", effectiveDate, i18n.locale);
    errors.acceptedTermsError = !acceptedTerms;
    if (errors.acceptedTermsError) {
      acceptedTermsErrorRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
    const hasErrors = Object.values(errors).some(Boolean);
    return hasErrors ? errors : false;
  };

  /**
   * Confirms the change request if validation passes, and sends the signage block to the server to be added to the changeRequest in S3
   */
  const confirmChangeRequest = async () => {
    const changeRequestUpdateData = {
      effectiveDate: dates.getUTC(changeRequest.effectiveDate),
      reasonForChange,
      signage: {
        name,
        signature,
        acceptedTerms,
        timestampOfAcceptance: dates.getUTC(),
        datedOn: dates.getUTC()
      }
    };
    const fieldErrors = await validateFields();
    if (fieldErrors !== false) {
      setErrorState({
        ...fieldErrors
      });
      return false;
    }
    try {
      setErrorState(initialErrorState);
      const success = await submitChangeRequest(changeRequestID, changeRequestUpdateData);
      if (success) {
        resetForm();
        onClose();
        return true;
      }
      return false;
    } catch (error) {
      deleteChangeRequest(changeRequestID);
      console.error("Error submitting change request:", error);
      return false;
    }
  };

  /**
   * Closes the change request after user confirmation.
   * 
   * This function prompts the user with a confirmation dialog to cancel the change request. 
   * If the user confirms, it deletes the change request using its UUID and then triggers the `onClose` callback.
   * 
   * @function
   */
  const closeIncompleteChangeRequest = () => {
    const cancel = window.confirm(i18n.t(tokens.labels.changeRequests.areYouSureYouWantToCancelTheChangeRequest));
    if (cancel) {
      deleteChangeRequest(changeRequestID);
      resetForm();
      onClose();
    }
  };
  return /*#__PURE__*/React.createElement(Modal, {
    open: open,
    title: i18n.t(tokens.titles.sendChangeRequest),
    onClose: closeIncompleteChangeRequest,
    primaryButton: {
      label: i18n.t(tokens.buttons.sendChangeRequest),
      onClick: confirmChangeRequest
    },
    loader: loader,
    style: {
      width: "1080px"
    }
  }, /*#__PURE__*/React.createElement(Text, {
    medium: true,
    light: true
  }, i18n.t(tokens.labels.changeRequests.requiredChangeRequestVerbiage)), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.name),
    value: changeRequest.name,
    onChange: e => handleInputChange('name', e.target.value),
    onBlur: () => onBlur('name', name),
    error: Boolean(nameError),
    helperText: nameError,
    fullWidth: true
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.signature),
    value: signature,
    onChange: e => handleInputChange('signature', e.target.value),
    onBlur: () => onBlur('signature', signature),
    error: Boolean(signatureError),
    helperText: signatureError,
    isSignature: true,
    fullWidth: true
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.changeRequests.reasonForChange),
    value: reasonForChange,
    onChange: e => handleInputChange('reasonForChange', e.target.value),
    onBlur: () => onBlur('reasonForChange', reasonForChange),
    error: Boolean(reasonForChangeError),
    helperText: reasonForChangeError,
    fullWidth: true
  }), /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.effectiveDate),
    value: changeRequest.effectiveDate,
    onChange: e => handleInputChange('effectiveDate', e.target.value),
    onBlur: () => onBlur('effectiveDate', effectiveDate),
    error: Boolean(effectiveDateError),
    helperText: effectiveDateError,
    fullWidth: true
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      flexDirection: "row",
      marginTop: 20
    }
  }, /*#__PURE__*/React.createElement(CustomWidthTooltip, {
    title: i18n.t(tokens.labels.changeRequests.examplesOfSupportingDocumentation),
    arrow: true,
    placement: "top-start"
  }, /*#__PURE__*/React.createElement(InfoIcon, {
    "aria-label": "info",
    style: {
      fontSize: 20,
      color: colors.mediumGrey,
      marginRight: 10
    }
  })), /*#__PURE__*/React.createElement(Text, null, i18n.t(tokens.labels.changeRequests.attachAnyNecessaryDocument))), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 10
    }
  }, changeRequestUUID ? /*#__PURE__*/React.createElement(Files, {
    noContainer: true,
    type: type,
    id: id,
    parentFolder: `changeRequests/${changeRequestUUID}/files`,
    dispatch: dispatch,
    listFiles: listFiles,
    createFolder: createFolder,
    uploadFile: uploadFile,
    downloadFile: downloadFile,
    removeFile: removeFile,
    disableCreateFolder: true,
    skipNotification: true
  }) : null), /*#__PURE__*/React.createElement(FormControl, {
    fullWidth: true,
    ref: acceptedTermsErrorRef
  }, acceptedTermsError ? /*#__PURE__*/React.createElement(FormHelperText, {
    error: true
  }, i18n.t(tokens.labels.banking.youMustAcceptTermsError)) : null, /*#__PURE__*/React.createElement(FormGroup, {
    row: true
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: Boolean(acceptedTerms),
      onChange: () => handleInputChange('acceptedTerms', !acceptedTerms)
    }),
    label: i18n.t(tokens.labels.changeRequests.changeRequestAuthorization)
  }))));
};
ChangeRequestModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  createChangeRequest: PropTypes.func.isRequired,
  deleteChangeRequest: PropTypes.func.isRequired,
  submitChangeRequest: PropTypes.func.isRequired,
  changeRequestData: PropTypes.object,
  loader: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  listFiles: PropTypes.func.isRequired,
  createFolder: PropTypes.func.isRequired,
  uploadFile: PropTypes.func.isRequired,
  downloadFile: PropTypes.func.isRequired,
  removeFile: PropTypes.func.isRequired
};
export default ChangeRequestModal;